import { Component, Input, OnInit, EventEmitter, Output, OnDestroy } from '@angular/core';
import { LoadingService } from '@app/services/loading.service';
import { unsubscribe } from '@app/utils';
import { FLOW_FOOTER, TypeLoading } from '@enums/index'
import {  Subscription } from 'rxjs';
/**
 * Componente que representa un pie de página personalizable.
 */
@Component({
    selector: 'app-am-footer',
    templateUrl: './am-footer.component.html',
    styleUrls: ['./am-footer.component.scss'],
    standalone: false
})
export class AmFooterComponent implements OnInit,OnDestroy {
  /**
   * Indica si el pie de página debe tener un borde superior.
   *
   * @type {boolean}
   * @default false
   */
  @Input() borderTop = false;

  /**
   * Define el flujo actual del pie de página (por ejemplo, 'checkout').
   *
   * @type {string}
   * @default ""
   */
  @Input() flow = "";

  /**
   * Indica si el botón "Continuar" debe ocupar todo el ancho disponible.
   *
   * @type {boolean}
   * @default false
   */
  @Input() continueFullSize = false;

  /**
   * Indica si el botón "Atrás" debe ocupar todo el ancho disponible.
   *
   * @type {boolean}
   * @default false
   */
  @Input() backFullSize = false;

  /**
   * Indica si el botón "Continuar" está deshabilitado.
   *
   * @type {boolean}
   * @default false
   */
  @Input() isDisableContinue = false;

  /**
   * Alias temporal para los templates que aún usan el typo histórico
   * `[isDisbaleContinue]`. Permite adoptar el nombre correcto de forma incremental sin
   * romper los callers existentes; los nuevos bindings deben usar `isDisableContinue`.
   *
   * TODO: eliminar el alias cuando todos los consumidores hayan sido migrados.
   */
  @Input('isDisbaleContinue')
  set legacyIsDisableContinue(value: boolean) {
    this.isDisableContinue = value;
  }

  /**
   * Indica si el botón "Atrás" está deshabilitado.
   *
   * @type {boolean}
   * @default false
   */
  @Input() isDisbaleBack = false;

  /**
   * Indica si se debe mostrar el botón "Agregar al carrito".
   *
   * @type {boolean}
   * @default false
   */
  @Input() showAddToCar = false;

  /**
   * Indica si se debe mostrar el botón "Atrás".
   *
   * @type {boolean}
   * @default false
   */
  @Input() showBack = false;

  /**
   * Indica si se debe mostrar el botón "Continuar".
   *
   * @type {boolean}
   * @default false
   */
  @Input() showContinue = false;

  /**
   * Indica si se debe mostrar el botón "Finalizar".
   *
   * @type {boolean}
   * @default false
   */
  @Input() showFinish = false;

  /**
   * Indica si se debe mostrar un botón opcional personalizado.
   *
   * @type {boolean}
   * @default false
   */
  @Input() showOptionalButton = false;

  /**
   * Indica si se debe mostrar el total en el pie de página.
   *
   * @type {boolean}
   * @default false
   */
  @Input() showTotal = false;

  /**
   * Texto personalizado para el botón "Continuar".
   *
   * @type {string}
   * @default 'continueCap'
   */
  @Input() textContinue = 'continueCap';

  /**
   * Texto personalizado para el botón "Finalizar".
   *
   * @type {string}
   * @default 'footer.finish'
   */
  @Input() textFinish = 'footer.finish';

  /**
   * Texto personalizado para el botón "Atrás".
   *
   * @type {string}
   * @default 'footer.back'
   */
  @Input() textOnBack = 'footer.back';

  /**
   * Texto personalizado para el botón opcional.
   *
   * @type {string}
   * @default 'CHANGE SEATS'
   */
  @Input() textOptionalButton = 'CHANGE SEATS';

  /**
   * Indica si se debe aplicar un tema oscuro al pie de página.
   *
   * @type {boolean}
   * @default false
   */
  @Input() themeDark = false;

  /**
   * Indica si se debe mostrar información sobre el peso del equipaje.
   *
   * @type {boolean}
   * @default false
   */
  @Input() weightBag = false;

  /**
   * Peso total del equipaje.
   *
   * @type {number}
   * @default 1
   */
  @Input() totalWeigthBag = 1;

  /**
   * Número actual de piezas de equipaje.
   *
   * @type {number}
   * @default 1
   */
  @Input() currentNumberWeightBag = 1;

  /**
   * Total a mostrar en el pie de página.
   *
   * @type {number}
   * @default 0
   */
  @Input() totalFooter = 0;

  /**
   * Código de la moneda para mostrar el total.
   *
   * @type {string}
   * @default 'MXN'
   */
  @Input() currencyCode: string = 'MXN';

  /**
   * Color de fondo personalizado para los botones.
   *
   * @type {string}
   * @default 'am-button-red'
   */
  @Input() backGroundColorButton: string = 'am-button-red';

  /**
   * Indica si se debe aplicar un tema rosa al pie de página.
   *
   * @type {boolean}
   * @default false
   */
  @Input() themePink = false;
  @Input() isOnBoarding:boolean = false;
  @Input() mybClosed:boolean = false;
  /**
   * Controla si el loader del botón continuar bloquea interacciones del resto de la pantalla.
   * @default true (mantiene el comportamiento previo para todas las pantallas que ya lo usaban).
   * Pasar `false` cuando se necesita el spinner dentro del botón SIN cubrir el resto de la UI
   * (por ejemplo, pantallas donde el usuario debe poder interactuar con elementos externos
   * mientras el loader está activo, como el modo simulador/test).
   */
  @Input() blockLoadingScreen = true;

  /**
   * EventEmitter que se activa cuando se hace clic en el botón "Agregar al carrito".
   *
   * @type {EventEmitter<any>}
   */
  @Output() private addToCar: EventEmitter<any>;

  /**
   * EventEmitter que se activa cuando se hace clic en el botón "Atrás".
   *
   * @type {EventEmitter<any>}
   */
  @Output() private back: EventEmitter<any>;

  /**
   * EventEmitter que se activa cuando se hace clic en el botón "Continuar".
   *
   * @type {EventEmitter<any>}
   */
  @Output() private continue: EventEmitter<any>;

  /**
   * EventEmitter que se activa cuando se hace clic en el botón opcional.
   *
   * @type {EventEmitter<any>}
   */
  @Output() private optinalButton: EventEmitter<any>;

  /**
   * EventEmitter que se activa cuando se hace clic en el botón "Revisar carrito".
   *
   * @type {EventEmitter<any>}
   */
  @Output() private reviewCart: EventEmitter<any>;

  /**
   * EventEmitter que se activa cuando se hace clic en el botón "Finalizar".
   *
   * @type {EventEmitter<any>}
   */
  @Output() private finish: EventEmitter<any>;

  /**
   * Tipo de flujo del pie de página.
   *
   * @type {FLOW_FOOTER}
   */
  flowType = FLOW_FOOTER;

  /**
   * Tema actual del pie de página.
   *
   * @type {string}
   */
  theme: string;

  /**
   * Indica si se debe mostrar el estado de carga.
   *
   * @type {boolean}
   * @default false
   */
  showLoading: boolean = false;

  /**
   * Arreglo de suscripciones para gestionar la desuscripción.
   *
   * @type {Subscription[]}
   */
  subs: Subscription[] = [];
  /**
   * Muestra el baner si se realizo cambio de irops
   */
  @Input() hasConfirmedIrops:boolean = false;
  constructor(
    private _loadingService: LoadingService,
  ) {
    this.addToCar = new EventEmitter();
    this.back = new EventEmitter();
    this.continue = new EventEmitter();
    this.optinalButton = new EventEmitter();
    this.reviewCart = new EventEmitter();
    this.finish = new EventEmitter();
  }

  ngOnInit(): void {
    this.theme = this.themeDark ? 'am-theme-dark' : 'am-theme-light';
    const subsLoading = this._loadingService.loadings[TypeLoading.FOOTER_LOADING].loading$.subscribe(loading => {
      this.showLoading = loading;
    });
    this.subs.push(subsLoading);
    if(this.isOnBoarding){
      this.textFinish='noticeClose.leave'
    }
    if(this.showFinish){
      this.textFinish = 'footer.finish2'
    }
  }

  ngOnDestroy() {
    unsubscribe(this.subs);
  }
  /**
   * Método que se ejecuta cuando se hace clic en el botón "Agregar al carrito".
   * Emite un evento a través del `EventEmitter` `addToCar`.
   *
   * @return {void} No retorna ningún valor.
   */
  onAddToCar(): void {
    this.addToCar.emit();
  }
  /**
   * Método que se ejecuta cuando se hace clic en el botón "Atrás".
   * Emite un evento a través del `EventEmitter` `back`.
   *
   * @return {void} No retorna ningún valor.
   */
  onBack(): void {
    this.back.emit();
  }

    /**
   * Método que se ejecuta cuando se hace clic en el botón "Continuar".
   * Emite un evento a través del `EventEmitter` `continue`, siempre y cuando no esté en estado de carga.
   *
   * @return {void} No retorna ningún valor.
   */

  onContinue(): void {
    if (!this.showLoading) {
      this.continue.emit();
    }
  }

   /**
   * Método que se ejecuta cuando se hace clic en el botón opcional.
   * Emite un evento a través del `EventEmitter` `optinalButton`.
   *
   * @return {void} No retorna ningún valor.
   */

  onOptional() {
    this.optinalButton.emit();
  }
 /**
   * Método que se ejecuta cuando se hace clic en el botón "Revisar carrito".
   * Emite un evento a través del `EventEmitter` `reviewCart`.
   *
   * @return {void} No retorna ningún valor.
   */
  onReviewCart(): void {
    this.reviewCart.emit();
  }
  /**
   * Método que se ejecuta cuando se hace clic en el botón "Finalizar".
   * Emite un evento a través del `EventEmitter` `finish`.
   *
   * @return {void} No retorna ningún valor.
   */
  onFinish(): void {
    this.finish.emit();
  }

}
